import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogContent, DialogTitle, DialogActions,
  Alert, Chip, Card, CardContent, Avatar, CircularProgress,
  Tooltip, Box, Typography, Pagination
} from '@mui/material';
import { Download, Filter } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://erpapi.eduskillsfoundation.org';
// const API_BASE_URL= 'http://localhost:8001'; 

const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam",
  "Bihar", "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

const APPLICANT_TYPE_LABELS = {
  '0': 'Campus Placement', '1': 'Job Holder', '2': 'Entrepreneur',
  '3': 'Higher Studies', '4': 'Job Seeker'
};

const APPLICANT_TYPES = [
  { value: '0', label: 'Campus Placement' }, { value: '1', label: 'Job Holder' },
  { value: '2', label: 'Entrepreneur' }, { value: '3', label: 'Higher Studies' },
  { value: '4', label: 'Job Seeker' }
];

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear + 1; i >= currentYear - 40; i--) years.push(i.toString());
  return years;
};

const YEAR_OPTIONS = generateYearOptions();

const truncateText = (text, maxLength = 20) => {
  if (!text) return 'N/A';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const RisingStar = () => {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [search, setSearch]       = useState('');
  const [finalYear, setFinalYear] = useState('');
  const [stateName, setStateName] = useState('');
  const [apType, setApType]       = useState('all');
  const [isSubmit, setIsSubmit]   = useState('all');

  const [openViewDialog, setOpenViewDialog]       = useState(false);
  const [selectedUser, setSelectedUser]           = useState(null);
  const [profilePicUrl, setProfilePicUrl]         = useState(null);
  const [profilePicLoading, setProfilePicLoading] = useState(false);
  const [downloadingImage, setDownloadingImage]   = useState(false);

  const [printingUserId, setPrintingUserId]   = useState(null);
  const [printStatusMap, setPrintStatusMap]   = useState({});
  const [printStatusLoading, setPrintStatusLoading] = useState({});

  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  const getAccessToken = () => localStorage.getItem('accessToken');
  const token = getAccessToken();

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 3000);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE_URL}${imagePath}`;
  };

  const getApTypeLabel = (apTypeValue) => APPLICANT_TYPE_LABELS[String(apTypeValue)] || 'N/A';

  const fetchPrintStatus = async (userId) => {
    setPrintStatusLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await axios.get(
        `${API_BASE_URL}/sucessstory/users/${userId}/print-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPrintStatusMap(prev => ({ ...prev, [userId]: response.data.is_print }));
      }
    } catch {
      // silently fail
    } finally {
      setPrintStatusLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const fetchData = async (overridePage = page) => {
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: (overridePage - 1) * rowsPerPage,
        publish_status: 'published'
      };
      if (search && search.trim())        params.search     = search.trim();
      if (finalYear && finalYear.trim())  params.final_year = finalYear.trim();
      if (stateName && stateName.trim())  params.state_name = stateName.trim();
      if (apType && apType !== 'all')     params.ap_type    = apType;
      if (isSubmit && isSubmit !== 'all') params.is_submit  = isSubmit === 'true';

      const response = await axios.get(`${API_BASE_URL}/sucessstory/profile`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const rows = response.data.data || [];
        setData(rows);
        setTotal(response.data.total || 0);
        setPrintStatusMap({});
        rows.forEach(row => fetchPrintStatus(row.user_id));
      }
    } catch (error) {
      showAlert(error.response?.data?.detail || 'Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
  }, [page, rowsPerPage, apType, isSubmit]);

  const handleFilter = () => { setPage(1); fetchData(1); };

  const handleResetFilters = () => {
    setSearch('');
    setFinalYear('');
    setStateName('');
    setApType('all');
    setIsSubmit('all');
    setPage(1);
    setTimeout(() => fetchData(1), 100);
  };

  const handlePrint = async (userId, currentIsPrint) => {
    const isCurrentlyPrinted = currentIsPrint === true || currentIsPrint === 1 || currentIsPrint === 'true' || currentIsPrint === '1';
    setPrintingUserId(userId);
    try {
      const payload = { is_print: !isCurrentlyPrinted };
      const response = await axios.put(
        `${API_BASE_URL}/sucessstory/users/${userId}/mark-print`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showAlert(isCurrentlyPrinted ? 'Unmarked print successfully!' : 'Marked as print successfully!');
        await fetchPrintStatus(userId);
      }
    } catch (error) {
      showAlert(error.response?.data?.detail || 'Error updating print status', 'error');
    } finally {
      setPrintingUserId(null);
    }
  };

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setOpenViewDialog(true);
    setProfilePicUrl(null);
    const fileKey = user?.success_story?.profile_pic;
    if (fileKey && fileKey !== '/images/default_profile_pic_url.png') {
      setProfilePicLoading(true);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/get-image-from-s3`,
          { file_key: fileKey },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProfilePicUrl(response.data.url);
      } catch {
        setProfilePicUrl(null);
      } finally {
        setProfilePicLoading(false);
      }
    }
  };

  const downloadImage = async (imagePath, userName) => {
    if (!imagePath || imagePath === '/images/default_profile_pic_url.png') {
      showAlert('No image available to download', 'warning');
      return;
    }
    setDownloadingImage(true);
    try {
      const imageUrl     = profilePicUrl || getImageUrl(imagePath);
      const extension    = imagePath.split('.').pop().split('?')[0].toLowerCase() || 'jpg';
      const safeUserName = userName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName     = `${safeUserName}_profile_${Date.now()}.${extension}`;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = imageUrl; });
      const canvas = document.createElement('canvas');
      canvas.width = img.width; canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link    = document.createElement('a');
        link.href = blobUrl; link.download = fileName;
        document.body.appendChild(link); link.click();
        setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 100);
        setDownloadingImage(false);
        showAlert(`Downloaded: ${fileName}`);
      }, `image/${extension}`);
    } catch {
      setDownloadingImage(false);
      showAlert('Download failed', 'error');
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={2}>Success Story Management</Typography>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>{alert.message}</Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small" placeholder="Search by Name/Email"
              value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Year</InputLabel>
              <Select value={finalYear} label="Year" onChange={(e) => setFinalYear(e.target.value)}>
                <MenuItem value=""><em>All Years</em></MenuItem>
                {YEAR_OPTIONS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>State</InputLabel>
              <Select value={stateName} label="State" onChange={(e) => setStateName(e.target.value)}>
                <MenuItem value=""><em>All States</em></MenuItem>
                {INDIAN_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select value={apType} label="Type" onChange={(e) => setApType(e.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                {APPLICANT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Submit</InputLabel>
              <Select value={isSubmit} label="Submit" onChange={(e) => setIsSubmit(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Submitted</MenuItem>
                <MenuItem value="false">Not Submitted</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<Filter size={16} />} onClick={handleFilter}>Filter</Button>
            <Button variant="outlined" onClick={handleResetFilters}>Reset</Button>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Submit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Print</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No published data found
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => {
                  const isSubmitted     = row.success_story?.is_submit || row.is_submit;
                  const isProcessing    = printingUserId === row.user_id;
                  const isStatusLoading = printStatusLoading[row.user_id];
                  const isPrintVal = printStatusMap.hasOwnProperty(row.user_id)
                    ? printStatusMap[row.user_id]
                    : (row.success_story?.is_print || false);
                  const isPrint = isPrintVal === true || isPrintVal === 1 || isPrintVal === 'true' || isPrintVal === '1';

                  return (
                    <TableRow key={row.user_id} hover>
                      <TableCell>{row.user_id}</TableCell>
                      <TableCell>
                        <Tooltip title={row.name || ''}>
                          <span>{truncateText(row.name, 15)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.email || ''}>
                          <span>{truncateText(row.email, 20)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{truncateText(row.state_name, 12)}</TableCell>
                      <TableCell>{row.final_year}</TableCell>
                      <TableCell>
                        <Chip label={getApTypeLabel(row.success_story?.ap_type)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isSubmitted ? 'Yes' : 'No'}
                          size="small"
                          color={isSubmitted ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {isStatusLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Chip
                            label={isPrint ? 'Printed' : 'Not Printed'}
                            size="small"
                            color={isPrint ? 'success' : 'default'}
                            variant={isPrint ? 'filled' : 'outlined'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button size="small" variant="outlined" onClick={() => handleViewUser(row)}>
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={isProcessing || isStatusLoading}
                            onClick={() => handlePrint(row.user_id, isPrint)}
                            startIcon={isProcessing || isStatusLoading ? <CircularProgress size={12} color="inherit" /> : null}
                            sx={{
                              minWidth: 90, fontWeight: 600,
                              cursor: 'pointer',
                              backgroundColor: isPrint ? '#16a34a' : '#1976d2',
                              color: '#fff',
                              '&:hover': { backgroundColor: isPrint ? '#15803d' : '#1565c0' },
                              '&.Mui-disabled': {
                                backgroundColor: '#b0bec5',
                                color: '#fff',
                                opacity: 0.7,
                              }
                            }}
                          >
                            {isProcessing ? 'Marking...' : isStatusLoading ? 'Loading...' : isPrint ? '✓ Done' : 'Print'}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Rows per page:</Typography>
            <FormControl size="small">
              <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(e.target.value); setPage(1); }}>
                {[10, 20, 25, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {total > 0 ? `${(page - 1) * rowsPerPage + 1}–${Math.min(page * rowsPerPage, total)} of ${total}` : '0 of 0'}
            </Typography>
            <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} size="small" color="primary" />
          </Box>
        </Box>
      </Card>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.4rem', fontWeight: 700 }}>User Details</DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Profile Picture */}
              {selectedUser.success_story?.profile_pic &&
               selectedUser.success_story.profile_pic !== '/images/default_profile_pic_url.png' && (
                <Box sx={{ textAlign: 'center' }}>
                  {profilePicLoading ? <CircularProgress /> : (
                    <Avatar
                      src={profilePicUrl || getImageUrl(selectedUser.success_story.profile_pic)}
                      alt={selectedUser.name}
                      sx={{ width: 120, height: 120, mx: 'auto', border: '4px solid #1976d2', fontSize: '2.5rem' }}
                    >
                      {selectedUser.name?.charAt(0)}
                    </Avatar>
                  )}
                  <Box mt={1}>
                    <Button
                      variant="outlined"
                      startIcon={downloadingImage ? <CircularProgress size={14} /> : <Download size={16} />}
                      onClick={() => downloadImage(selectedUser.success_story.profile_pic, selectedUser.name)}
                      disabled={downloadingImage || profilePicLoading}
                      size="small"
                    >
                      {downloadingImage ? 'Downloading...' : 'Download Image'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Basic Info */}
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ borderBottom: '2px solid #1976d2', pb: 0.5, mb: 2 }}>
                  Basic Information
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {[
                    ['Full Name', selectedUser.name],
                    ['Email', selectedUser.email],
                    ['Mobile', selectedUser.mobile],
                    ['Gender', selectedUser.gender],
                    ['Date of Birth', selectedUser.dob],
                    ['AICTE ID', selectedUser.aicte_id],
                    ['Caste', selectedUser.caste],
                    ['EWS Status', selectedUser.ews],
                    ['Family Income', selectedUser.family_income],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={500}>{value || 'N/A'}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Academic Info */}
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ borderBottom: '2px solid #1976d2', pb: 0.5, mb: 2 }}>
                  Academic Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ gridColumn: 'span 2' }}>
                    <Typography variant="caption" color="text.secondary">Institute Name</Typography>
                    <Typography variant="body2" fontWeight={500}>{selectedUser.institute_name || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">State</Typography>
                    <Typography variant="body2" fontWeight={500}>{selectedUser.state_name || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Final Year</Typography>
                    <Typography variant="body2" fontWeight={500}>{selectedUser.final_year || 'N/A'}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* ✅ Completed Domains */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ borderBottom: '2px solid #1976d2', pb: 0.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  Completed Domains
                  <Chip
                    label={selectedUser.success_story?.completed_domains?.length || 0}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Typography>

                {selectedUser.success_story?.completed_domains?.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedUser.success_story.completed_domains.map((domain) => (
                      <Chip
                        key={domain.domain_id}
                        label={domain.domain_name}
                        size="small"
                        sx={{
                          backgroundColor: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          '&:hover': { backgroundColor: '#dbeafe' }
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No completed domains found (is_issue &gt; 1)
                  </Typography>
                )}
              </Box>

              {/* Success Story */}
              {selectedUser.success_story && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ borderBottom: '2px solid #1976d2', pb: 0.5, mb: 2 }}>
                    Success Story Details
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Applicant Type</Typography>
                      <Box mt={0.5}><Chip label={getApTypeLabel(selectedUser.success_story.ap_type)} size="small" variant="outlined" /></Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Submit Status</Typography>
                      <Box mt={0.5}>
                        <Chip
                          label={selectedUser.success_story.is_submit ? 'Submitted' : 'Not Submitted'}
                          size="small"
                          color={selectedUser.success_story.is_submit ? 'success' : 'default'}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Print Status</Typography>
                      <Box mt={0.5}>
                        <Chip
                          label={
                            printStatusMap.hasOwnProperty(selectedUser.user_id)
                              ? (printStatusMap[selectedUser.user_id] ? 'Printed' : 'Not Printed')
                              : (selectedUser.success_story.is_print ? 'Printed' : 'Not Printed')
                          }
                          size="small"
                          color={
                            (printStatusMap[selectedUser.user_id] ?? selectedUser.success_story.is_print)
                              ? 'success' : 'default'
                          }
                          variant={
                            (printStatusMap[selectedUser.user_id] ?? selectedUser.success_story.is_print)
                              ? 'filled' : 'outlined'
                          }
                        />
                      </Box>
                    </Box>
                    {selectedUser.success_story.package && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Package</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedUser.success_story.package}</Typography>
                      </Box>
                    )}
                    {selectedUser.success_story.designation && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Designation</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedUser.success_story.designation}</Typography>
                      </Box>
                    )}
                    {selectedUser.success_story.cname && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Company Name</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedUser.success_story.cname}</Typography>
                      </Box>
                    )}
                    {selectedUser.success_story.caddress && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Company Address</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedUser.success_story.caddress}</Typography>
                      </Box>
                    )}
                    {selectedUser.success_story.linkedin_url && (
                      <Box sx={{ gridColumn: 'span 2' }}>
                        <Typography variant="caption" color="text.secondary">LinkedIn URL</Typography>
                        <Typography variant="body2">
                          <a href={selectedUser.success_story.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                            {selectedUser.success_story.linkedin_url}
                          </a>
                        </Typography>
                      </Box>
                    )}
                    {selectedUser.success_story.briefly_desc && (
                      <Box sx={{ gridColumn: 'span 2' }}>
                        <Typography variant="caption" color="text.secondary">Brief Description</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedUser.success_story.briefly_desc}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RisingStar;